/**
 * Pagination Component
 *
 * Page navigation with items per page selector
 * Task: T4.4.6 - Pagination component
 */

import {
  InlineStack,
  Button,
  Text,
  Select,
  Box,
} from '@shopify/polaris';
import { ChevronLeftIcon, ChevronRightIcon } from '@shopify/polaris-icons';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  showItemsPerPage?: boolean;
  itemsPerPageOptions?: number[];
}

export function Pagination({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  showItemsPerPage = true,
  itemsPerPageOptions = [12, 24, 48],
}: PaginationProps) {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const itemsPerPageSelectOptions = itemsPerPageOptions.map((value) => ({
    label: `${value} per page`,
    value: value.toString(),
  }));

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage > 3) {
        pages.push('...');
      }

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push('...');
      }

      pages.push(totalPages);
    }

    return pages;
  };

  if (totalPages <= 1 && !showItemsPerPage) {
    return null;
  }

  return (
    <Box paddingBlock="400">
      <InlineStack align="space-between" blockAlign="center">
        {/* Items info */}
        <Text as="p" variant="bodySm" tone="subdued">
          Showing {startItem}-{endItem} of {totalItems} items
        </Text>

        {/* Page navigation */}
        <InlineStack gap="200" blockAlign="center">
          <Button
            icon={ChevronLeftIcon}
            disabled={currentPage <= 1}
            onClick={handlePrevious}
            accessibilityLabel="Previous page"
          />

          {getPageNumbers().map((page, index) => (
            typeof page === 'number' ? (
              <Button
                key={`page-${page}`}
                variant={currentPage === page ? 'primary' : 'secondary'}
                onClick={() => onPageChange(page)}
              >
                {String(page)}
              </Button>
            ) : (
              <Text key={`ellipsis-${index}`} as="span" variant="bodySm">
                {page}
              </Text>
            )
          ))}

          <Button
            icon={ChevronRightIcon}
            disabled={currentPage >= totalPages}
            onClick={handleNext}
            accessibilityLabel="Next page"
          />
        </InlineStack>

        {/* Items per page selector */}
        {showItemsPerPage && onItemsPerPageChange && (
          <Box minWidth="120px">
            <Select
              label="Items per page"
              labelHidden
              options={itemsPerPageSelectOptions}
              value={itemsPerPage.toString()}
              onChange={(value) => onItemsPerPageChange(parseInt(value, 10))}
            />
          </Box>
        )}
      </InlineStack>
    </Box>
  );
}
